const chapter = {
  id: 'centre-of-mass-collisions',
  slug: 'centre-of-mass-collisions',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE / NEET',
  title: {
    english: 'Centre of Mass & Collisions',
        hindi: 'द्रव्यमान केन्द्र और टक्कर',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2-3 questions in JEE Main / Adv',
        hindi: 'जेईई मेन/एड में 2-3 प्रश्न',
  },
  syllabusTags: ['Mechanics', 'Class-11', 'JEE Main', 'NEET'],
  sections: [
    {
      id: 'centre-of-mass',
      title: {
        english: 'Centre of Mass (COM)',
        hindi: 'द्रव्यमान केन्द्र (COM)',
      },
      blocks: [
        {
          type: 'definition',
          title: {
            english: 'Centre of Mass',
        hindi: 'मास का केंद्र',
          },
          body: {
            english: 'For a system of particles, $\\vec r_{cm} = \\dfrac{\\sum m_i \\vec r_i}{\\sum m_i}$. For a continuous body, $\\vec r_{cm} = \\dfrac{1}{M}\\int \\vec r \\,dm$.',
        hindi: 'कणों की एक प्रणाली के लिए, $\\vec r_{cm} = \\dfrac{\\sum m_i \\vec r_i}{\\sum m_i}$। एक सतत पिंड के लिए, $\\vec r_{cm} = \\dfrac{1}{M}\\int \\vec r \\,dm$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'COM lies on any axis of symmetry; at intersection of axes if multiple.',
        hindi: 'COM समरूपता के किसी भी अक्ष पर स्थित है; अक्षों के प्रतिच्छेदन पर यदि एकाधिक हो।',
            },
            {
              english: 'COM of uniform rod: midpoint. Of triangular plate: centroid (intersection of medians, $1/3$ of each median from base).',
        hindi: 'एकसमान छड़ का COM: मध्यबिंदु। त्रिकोणीय प्लेट का: केन्द्रक (मध्यरेखाओं का प्रतिच्छेदन, आधार से प्रत्येक मध्यिका का $1/3$)।',
            },
            {
              english: 'COM of uniform semicircular wire (radius $R$): $\\dfrac{2R}{\\pi}$ from diameter.',
        hindi: 'एकसमान अर्धवृत्ताकार तार का COM (त्रिज्या $R$): व्यास से $\\dfrac{2R}{\\pi}$।',
            },
            {
              english: 'COM of uniform semicircular disc: $\\dfrac{4R}{3\\pi}$ from diameter.',
        hindi: 'एकसमान अर्धवृत्ताकार डिस्क का COM: व्यास से $\\dfrac{4R}{3\\pi}$।',
            },
            {
              english: 'COM of uniform hemispherical solid: $\\dfrac{3R}{8}$ from base. Hemispherical shell: $R/2$.',
        hindi: 'एक समान अर्धगोलाकार ठोस का COM: आधार से $\\dfrac{3R}{8}$। अर्धगोलाकार खोल: $R/2$.',
            },
            {
              english: 'COM of uniform cone (solid): $h/4$ from base; (hollow surface): $h/3$.',
        hindi: 'एकसमान शंकु का COM (ठोस): आधार से $h/4$; (खोखली सतह): $h/3$.',
            },
          ],
        },
        {
          type: 'equation',
          equation: '\\vec v_{cm} = \\dfrac{\\sum m_i \\vec v_i}{M}, \\quad \\vec a_{cm} = \\dfrac{\\sum m_i \\vec a_i}{M} = \\dfrac{\\vec F_{ext}}{M}',
        },
        {
          type: 'note',
          body: {
            english: 'Internal forces cancel in pairs. Only **external forces** affect COM motion. If $\\vec F_{ext} = 0$, COM moves with constant velocity (or stays at rest).',
        hindi: 'आंतरिक बल जोड़े में रद्द होते हैं। केवल **बाहरी ताकतें** COM गति को प्रभावित करती हैं। यदि $\\vec F_{ext} = 0$, COM निरंतर वेग से चलता है (या आराम पर रहता है)।',
          },
        },
      ],
    },
    {
      id: 'cavity-trick',
      title: {
        english: 'COM with Cavities (Negative Mass Trick)',
        hindi: 'कोटरों सहित COM (ऋणात्मक द्रव्यमान युक्ति)',
      },
      blocks: [
        {
          type: 'paragraph',
          body: {
            english: 'Body with cavity = original body + (negative mass cavity). Use $\\vec r_{cm} = \\dfrac{m_1 \\vec r_1 - m_2 \\vec r_2}{m_1 - m_2}$ where $m_1$ is original, $m_2$ is removed mass.',
        hindi: 'गुहा वाला शरीर = मूल शरीर + (नकारात्मक द्रव्यमान गुहा)। $\\vec r_{cm} = \\dfrac{m_1 \\vec r_1 - m_2 \\vec r_2}{m_1 - m_2}$ का उपयोग करें जहां $m_1$ मूल है, $m_2$ को हटा दिया गया है।',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'A uniform disc of radius $R$ has a circular hole of radius $R/2$ cut out, with the hole centred at distance $R/2$ from the disc centre. Find new COM.',
        hindi: 'त्रिज्या $R$ की एक समान डिस्क में त्रिज्या $R/2$ का एक गोलाकार छेद काटा गया है, छेद डिस्क केंद्र से दूरी $R/2$ पर केंद्रित है। नया COM खोजें.',
          },
          solution: {
            english: 'Mass of original disc $\\propto R^2$, removed disc $\\propto (R/2)^2 = R^2/4$. $x_{cm} = \\dfrac{R^2 \\cdot 0 - (R^2/4)(R/2)}{R^2 - R^2/4} = \\dfrac{-R^3/8}{3R^2/4} = -R/6$. So COM shifts $R/6$ **opposite** to hole.',
        hindi: 'मूल डिस्क का द्रव्यमान $\\propto R^2$, हटाई गई डिस्क $\\propto (R/2)^2 = R^2/4$। $x_{cm} = \\dfrac{R^2 \\cdot 0 - (R^2/4)(R/2)}{R^2 - R^2/4} = \\dfrac{-R^3/8}{3R^2/4} = -R/6$. तो COM $R/6$ **विपरीत** को छेद में स्थानांतरित कर देता है।',
          },
        },
      ],
    },
    {
      id: 'momentum-conservation',
      title: {
        english: 'Linear Momentum & Conservation',
        hindi: 'रैखिक संवेग और संरक्षण',
      },
      blocks: [
        {
          type: 'equation',
          equation: '\\vec p = m\\vec v, \\quad \\vec F = \\dfrac{d\\vec p}{dt}',
        },
        {
          type: 'paragraph',
          body: {
            english: '**Conservation:** if $\\vec F_{ext} = 0$, total $\\vec p$ is conserved. Equivalently $\\vec v_{cm}$ stays constant.',
        hindi: '**संरक्षण:** यदि $\\vec F_{ext} = 0$, तो कुल $\\vec p$ संरक्षित है। समान रूप से $\\vec v_{cm}$ स्थिर रहता है।',
          },
        },
        {
          type: 'example',
          problem: {
            english: 'A bomb of mass $5$ kg at rest explodes into 2 fragments of masses $2$ kg and $3$ kg. The $2$ kg piece moves at $9$ m/s east. Find velocity of $3$ kg piece.',
        hindi: '$5$ किलोग्राम द्रव्यमान का एक बम आराम करने पर $2$ किलोग्राम और $3$ किलोग्राम द्रव्यमान के 2 टुकड़ों में फट जाता है। $2$ किग्रा का टुकड़ा $9$ m/s पूर्व की ओर बढ़ता है। $3$ किग्रा टुकड़े का वेग ज्ञात कीजिए।',
          },
          solution: {
            english: '$0 = 2(9) + 3v \\Rightarrow v = -6$ m/s (i.e. $6$ m/s west).',
        hindi: '$0 = 2(9) + 3v \\Rightarrow v = -6$ मी/से (अर्थात् $6$ मी/से पश्चिम)।',
          },
        },
      ],
    },
    {
      id: 'collisions',
      title: {
        english: 'Collisions — Classification',
        hindi: 'टक्कर — वर्गीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Elastic:** KE conserved AND momentum conserved. $e = 1$.',
        hindi: '**लोचदार:** केई संरक्षित और गति संरक्षित। $e = 1$.',
            },
            {
              english: '**Inelastic:** momentum conserved but KE NOT. $0 < e < 1$.',
        hindi: '**इनैलास्टिक:** संवेग संरक्षित लेकिन KE नहीं। $0 < e < 1$.',
            },
            {
              english: '**Perfectly inelastic:** bodies stick → max KE loss. $e = 0$.',
        hindi: '**पूरी तरह से बेलोचदार:** शरीर चिपकते हैं → अधिकतम केई हानि। $e = 0$.',
            },
            {
              english: '**Coefficient of restitution** $e = \\dfrac{\\text{velocity of separation}}{\\text{velocity of approach}}$ (along line of impact).',
        hindi: '**पुनर्स्थापना का गुणांक** $e = \\dfrac{\\text{वेग of separation}}{\\text{वेग of approach}}$ (प्रभाव की रेखा के साथ)।',
            },
          ],
        },
      ],
    },
    {
      id: '1d-elastic',
      title: {
        english: '1-D Elastic Collision Formulas',
        hindi: '1-D प्रत्यास्थ टक्कर सूत्र',
      },
      blocks: [
        {
          type: 'equation',
          equation: 'v_1\' = \\dfrac{(m_1 - m_2) u_1 + 2 m_2 u_2}{m_1 + m_2}',
        },
        {
          type: 'equation',
          equation: 'v_2\' = \\dfrac{(m_2 - m_1) u_2 + 2 m_1 u_1}{m_1 + m_2}',
        },
        {
          type: 'list',
          items: [
            {
              english: '**Equal masses ($m_1 = m_2$):** velocities are exchanged.',
        hindi: '**समान द्रव्यमान ($m_1 = m_2$):** वेगों का आदान-प्रदान होता है।',
            },
            {
              english: '**Heavy hits light at rest:** $v_1\' \\approx u_1$, $v_2\' \\approx 2u_1$ (light flies off at twice).',
        hindi: '**विराम के समय प्रकाश पर भारी प्रहार:** $v_1\\\' \\approx u_1$, $v_2\\\' \\approx 2u_1$ (प्रकाश दो बार में उड़ जाता है)।',
            },
            {
              english: '**Light hits heavy at rest:** $v_1\' \\approx -u_1$ (rebounds), $v_2\' \\approx 0$.',
        hindi: '**आराम के समय प्रकाश जोर से टकराता है:** $v_1\\\' \\approx -u_1$ (रिबाउंड), $v_2\\\' \\approx 0$।',
            },
          ],
        },
        {
          type: 'example',
          problem: {
            english: 'A $2$ kg ball at $4$ m/s elastically hits a $3$ kg ball at rest. Find final velocities.',
        hindi: 'एक $2$ किग्रा की गेंद $4$ m/s पर आराम से $3$ किग्रा की गेंद से टकराती है। अंतिम वेग ज्ञात कीजिए।',
          },
          solution: {
            english: '$v_1\' = (2-3)(4)/5 = -0.8$ m/s; $v_2\' = 2(2)(4)/5 = 3.2$ m/s.',
        hindi: '$v_1\' = (2-3)(4)/5 = -0.8$ मी/से; $v_2\' = 2(2)(4)/5 = 3.2$ मी/से.',
          },
        },
      ],
    },
    {
      id: 'inelastic',
      title: {
        english: 'Inelastic & 2-D Collisions',
        hindi: 'अप्रत्यास्थ और 2-D टक्करें',
      },
      blocks: [
        {
          type: 'equation',
          equation: 'v_1\' = \\dfrac{(m_1 - e m_2)u_1 + (1+e)m_2 u_2}{m_1 + m_2}',
        },
        {
          type: 'equation',
          equation: '\\Delta KE = \\dfrac{1}{2} \\dfrac{m_1 m_2}{m_1 + m_2}(1 - e^2)(u_1 - u_2)^2 \\quad \\text{(KE lost)}',
        },
        {
          type: 'paragraph',
          body: {
            english: '**Perfectly inelastic ($e=0$, stick):** $v = (m_1 u_1 + m_2 u_2)/(m_1 + m_2)$. Maximum KE loss = $\\frac{1}{2}\\frac{m_1 m_2}{m_1+m_2}(u_1-u_2)^2$.',
        hindi: '**पूरी तरह से बेलोचदार ($e=0$, छड़ी):** $v = (m_1 u_1 + m_2 u_2)/(m_1 + m_2)$। अधिकतम KE हानि = $\\frac{1}{2}\\frac{m_1 m_2}{m_1+m_2}(u_1-u_2)^2$.',
          },
        },
        {
          type: 'note',
          body: {
            english: 'For a ball dropped from height $h$ rebounding to $h\'$: $e = \\sqrt{h\'/h}$. After $n$ bounces $h_n = e^{2n} h$.',
        hindi: 'ऊंचाई $h$ से गिरी हुई गेंद के लिए जो $h\\\'$ पर वापस लौटती है: $e = \\sqrt{h\\\'/h}$। $n$ के बाउंस होने के बाद $h_n = e^{2n} h$। **2-डी टकराव:** $h$ और $h\\\'$ के साथ **अलग से** संवेग संरक्षण लागू करें। आराम पर एक शरीर के साथ तिरछी लोचदार के लिए, अंतिम वेगों के बीच का कोण $e = \\sqrt{h\\\'/h}$ है यदि द्रव्यमान समान हैं।',
          },
        },
        {
          type: 'paragraph',
          body: {
            english: '**2-D collisions:** apply momentum conservation **separately** along $x$ and $y$. For oblique elastic with one body at rest, the angle between final velocities is $90°$ if masses are equal.',
        hindi: '**2-डी टकराव:** $x$ और $y$ के साथ **अलग से** संवेग संरक्षण लागू करें। आराम पर एक शरीर के साथ तिरछी लोचदार के लिए, अंतिम वेगों के बीच का कोण $90°$ है यदि द्रव्यमान समान हैं।',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\vec r_{cm} = \\sum m_i \\vec r_i / M',
      desc: {
        english: '',
        hindi: 'आर सेमी = एम आई आर आई / एम',
      },
    },
    {
      latex: 'M \\vec a_{cm} = \\vec F_{ext}',
      desc: {
        english: '',
        hindi: 'एम ए सेमी = एफ एक्सटेंशन',
      },
    },
    {
      latex: 'v_1\' = ((m_1-m_2)u_1 + 2m_2 u_2)/(m_1+m_2)',
      desc: {
        english: '1-D elastic',
        hindi: '1-डी इलास्टिक',
      },
    },
    {
      latex: 'e = (v_2\' - v_1\')/(u_1 - u_2)',
      desc: {
        english: 'Coefficient of restitution',
        hindi: 'क्षतिपूर्ति का गुणांक',
      },
    },
    {
      latex: '\\Delta KE = \\tfrac12 (m_1 m_2/M)(1-e^2)(u_1-u_2)^2',
      desc: {
        english: 'KE loss',
        hindi: 'केई हानि',
      },
    },
    {
      latex: 'v_{stick} = (m_1 u_1 + m_2 u_2)/(m_1+m_2)',
      desc: {
        english: 'Perfectly inelastic',
        hindi: 'बिल्कुल बेलोचदार',
      },
    },
    {
      latex: 'h_n = e^{2n} h_0',
      desc: {
        english: 'Bounce height',
        hindi: 'उछाल ऊंचाई',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'COM',
      problem: {
        english: 'Two particles of masses $1$ kg and $2$ kg at $(1,0)$ and $(0,3)$. Find COM.',
        hindi: 'द्रव्यमान के दो कण $1$ kg और $2$ kg $(1,0)$ और $(0,3)$ पर। कॉम खोजें.',
      },
      answer: {
        english: '$(1/3, 2)$',
        hindi: '$(1/3, 2)$',
      },
      solution: {
        english: '$(1\\cdot1 + 2\\cdot0)/3, (1\\cdot0+2\\cdot3)/3$.',
        hindi: '$(1\\cdot1 + 2\\cdot0)/3, (1\\cdot0+2\\cdot3)/3$.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Elastic',
      problem: {
        english: 'A body of mass $m$ moving with velocity $v$ collides elastically with stationary body of same mass. After collision, velocity of first body?',
        hindi: '$m$ द्रव्यमान का एक पिंड $v$ वेग से चलते हुए समान द्रव्यमान के स्थिर पिंड से प्रत्यास्थ रूप से टकराता है। टक्कर के बाद पहले पिंड का वेग?',
      },
      answer: {
        english: '0',
        hindi: '0',
      },
      solution: {
        english: 'Equal masses → velocity exchange.',
        hindi: 'समान द्रव्यमान → वेग विनिमय।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Inelastic',
      problem: {
        english: 'A bullet of $10$ g at $500$ m/s embeds in a $1$ kg block at rest. Find common velocity.',
        hindi: '$500$ m/s पर $10$ ग्राम की एक गोली आराम के समय $1$ किलोग्राम ब्लॉक में समा जाती है। उभयनिष्ठ वेग ज्ञात कीजिए।',
      },
      answer: {
        english: '$\\approx 4.95$ m/s',
        hindi: '$\\approx 4.95$ m/s',
      },
      solution: {
        english: '$0.01(500) = 1.01 v \\Rightarrow v \\approx 4.95$.',
        hindi: '$0.01(500) = 1.01 v \\Rightarrow v \\approx 4.95$.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Restitution',
      problem: {
        english: 'A ball dropped from $20$ m rebounds to $5$ m. Find $e$.',
        hindi: 'एक गेंद $20$ मीटर से गिरकर $5$ मीटर पर वापस आ जाती है। $e$ खोजें।',
      },
      answer: {
        english: '$1/2$',
        hindi: '$1/2$',
      },
      solution: {
        english: '$e = \\sqrt{5/20} = 1/2$.',
        hindi: '$e = \\sqrt{5/20} = 1/2$.',
      },
    },
    {
      year: 2019,
      exam: 'JEE Adv',
      tag: '2-D',
      problem: {
        english: 'Two equal masses with one at rest collide elastically obliquely. Angle between final velocities?',
        hindi: 'दो समान द्रव्यमान जिनमें से एक स्थिर अवस्था में है, प्रत्यास्थ रूप से टकराते हैं। अंतिम वेगों के बीच का कोण?',
      },
      answer: {
        english: '$90°$',
        hindi: '$90°$',
      },
      solution: {
        english: 'Standard result for equal-mass oblique elastic collision.',
        hindi: 'समान-द्रव्यमान तिरछी लोचदार टक्कर के लिए मानक परिणाम।',
      },
    },
  ],
  next: {
    slug: 'fluid-dynamics',
    title: 'Fluid Dynamics',
  },
};

export default chapter;
